import { useState, useEffect } from 'react';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, Pencil, Trash2, Save, Search, Filter, MoreVertical, RefreshCw,
  IndianRupee, Tag, Package, Images, X, GripVertical, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { ImageUploader } from '../../components/ui/ImageUploader';
import { AdvancedImageUploader } from '../../components/ui/AdvancedImageUploader';

const categories = ['Shower Enclosures', 'Glass Partitions', 'Partitions', 'Railings', 'Office', 'Windows', 'Doors', 'Front Elevation'];
const priceUnits = ['per sq ft', 'per piece', 'per meter', 'per set', 'per running ft'];
const stockStatuses = [
  { value: 'available', label: 'Available' },
  { value: 'on_enquiry', label: 'Available on Enquiry' },
  { value: 'out_of_stock', label: 'Out of Stock' }
];

const defaultProduct = {
  id: '', name: '', price: 0, price_max: null, price_unit: 'per sq ft',
  category: 'Partitions', description: '', short_description: '',
  image: '', images: [], videos: [], tags: [], is_featured: false, show_on_homepage: true,
  stock_status: 'available', order: 0, is_active: true, seo_title: '', seo_description: ''
};

export const ProductsManager = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const res = await apiService.getProducts(token);
      setProducts(res.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 50);

  const handleSave = async () => {
    const { item } = editModal;
    if (!item.id || !item.name || !item.price) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const cleanItem = { ...item, tags: item.tags.filter(t => t.trim()) };
      if (item.isNew) {
        await apiService.createProduct(token, cleanItem);
        toast.success('Product created successfully!');
      } else {
        await apiService.updateProduct(token, item.id, cleanItem);
        toast.success('Product updated successfully!');
      }
      setEditModal({ open: false, item: null });
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving product');
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteProduct(token, deleteDialog.item.id);
      toast.success('Product deleted');
      setDeleteDialog({ open: false, item: null });
      fetchProducts();
    } catch (error) {
      toast.error('Error deleting product');
    }
  };

  const openEditModal = (item = null) => {
    setEditModal({
      open: true,
      item: item ? { ...item, tags: item.tags || [], images: item.images || [], videos: item.videos || [], isNew: false } : { ...defaultProduct, id: `product-${Date.now()}`, order: products.length + 1, tags: [], images: [], videos: [], isNew: true }
    });
  };

  const filtered = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchCategory = filterCategory === 'all' || p.category === filterCategory;
    return matchSearch && matchCategory;
  });

  const getStockBadge = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-700';
      case 'on_enquiry': return 'bg-yellow-100 text-yellow-700';
      case 'out_of_stock': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="space-y-6" data-testid="products-manager">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">Products Manager</h1>
          <p className="text-slate-500">Manage your product catalog</p>
        </div>
        <Button onClick={() => openEditModal()} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" /> Add Product
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchProducts}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No products found</p>
          <Button onClick={() => openEditModal()} variant="outline" className="mt-4"><Plus className="w-4 h-4 mr-2" /> Add Product</Button>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((product) => (
            <Card key={product.id} className={`overflow-hidden ${!product.is_active ? 'opacity-60' : ''}`}>
              <div className="aspect-square relative bg-slate-100">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                  {product.is_featured && <span className="px-2 py-1 bg-orange-500 text-white text-xs rounded">Featured</span>}
                  <span className={`px-2 py-1 text-xs rounded ${getStockBadge(product.stock_status)}`}>
                    {stockStatuses.find(s => s.value === product.stock_status)?.label}
                  </span>
                </div>
                {/* Image count badge */}
                {(product.images?.length > 0) && (
                  <div className="absolute bottom-2 left-2">
                    <span className="px-2 py-1 bg-black/70 text-white text-xs rounded flex items-center gap-1">
                      <Images className="w-3 h-3" />
                      {1 + product.images.length} images
                    </span>
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button size="icon" variant="secondary" className="w-8 h-8"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditModal(product)}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteDialog({ open: true, item: product })} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-xs text-sky-600 font-medium">{product.category}</p>
                <h3 className="font-semibold text-slate-900 line-clamp-1">{product.name}</h3>
                <p className="text-sm text-slate-500 line-clamp-2 mt-1">{product.short_description || product.description}</p>
                <p className="text-xl font-bold text-sky-600 mt-2">
                  ₹{product.price}{product.price_max && ` - ₹${product.price_max}`}
                  <span className="text-sm font-normal text-slate-500">/{product.price_unit}</span>
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, item: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editModal.item?.isNew ? 'Add New Product' : 'Edit Product'}</DialogTitle>
            <DialogDescription>Fill in the product details below</DialogDescription>
          </DialogHeader>
          
          {editModal.item && (
            <div className="space-y-6 py-4">
              {/* Main Product Image */}
              <ImageUploader
                label="Main Product Image *"
                value={editModal.item.image}
                onChange={(url) => setEditModal({ ...editModal, item: { ...editModal.item, image: url } })}
                aspectRatio={1}
                token={token}
              />

              {/* Advanced Image Gallery with Watermark & Full Upload */}
              <AdvancedImageUploader
                label="Product Gallery (Drag & Drop Multiple Images)"
                images={editModal.item.images?.map(url => ({ url })) || []}
                onImagesChange={(imgs) => setEditModal({ 
                  ...editModal, 
                  item: { ...editModal.item, images: imgs.map(i => i.url) } 
                })}
                maxImages={15}
                showWatermark={true}
                defaultWatermarkText="Raj Aluminium"
                token={token}
              />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Product ID *</Label>
                  <Input value={editModal.item.id} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, id: e.target.value } })} disabled={!editModal.item.isNew} />
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={editModal.item.category} onValueChange={(value) => setEditModal({ ...editModal, item: { ...editModal.item, category: value } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Product Name *</Label>
                <Input
                  value={editModal.item.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    const updates = { name };
                    if (editModal.item.isNew) updates.id = generateSlug(name);
                    setEditModal({ ...editModal, item: { ...editModal.item, ...updates } });
                  }}
                  placeholder="e.g., Premium Glass Partition"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Price (₹) *</Label>
                  <Input type="number" value={editModal.item.price} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, price: parseFloat(e.target.value) || 0 } })} />
                </div>
                <div className="space-y-2">
                  <Label>Max Price (₹)</Label>
                  <Input type="number" value={editModal.item.price_max || ''} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, price_max: parseFloat(e.target.value) || null } })} placeholder="Optional" />
                </div>
                <div className="space-y-2">
                  <Label>Price Unit</Label>
                  <Select value={editModal.item.price_unit} onValueChange={(value) => setEditModal({ ...editModal, item: { ...editModal.item, price_unit: value } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{priceUnits.map(unit => <SelectItem key={unit} value={unit}>{unit}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Short Description</Label>
                <Input value={editModal.item.short_description} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, short_description: e.target.value } })} placeholder="Brief description" maxLength={100} />
              </div>

              <div className="space-y-2">
                <Label>Full Description *</Label>
                <Textarea value={editModal.item.description} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, description: e.target.value } })} placeholder="Detailed product description..." rows={3} />
              </div>

              <div className="space-y-2">
                <Label>Tags (comma separated)</Label>
                <Input value={editModal.item.tags?.join(', ')} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, tags: e.target.value.split(',').map(t => t.trim()) } })} placeholder="glass, partition, office" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Stock Status</Label>
                  <Select value={editModal.item.stock_status} onValueChange={(value) => setEditModal({ ...editModal, item: { ...editModal.item, stock_status: value } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{stockStatuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input type="number" value={editModal.item.order} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, order: parseInt(e.target.value) || 0 } })} />
                </div>
              </div>

              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Switch checked={editModal.item.is_active} onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, is_active: checked } })} />
                  <Label>Active</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editModal.item.is_featured} onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, is_featured: checked } })} />
                  <Label>Featured</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editModal.item.show_on_homepage} onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, show_on_homepage: checked } })} />
                  <Label>Show on Homepage</Label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, item: null })}>Cancel</Button>
            <Button onClick={handleSave} className="bg-sky-500 hover:bg-sky-600"><Save className="w-4 h-4 mr-2" /> Save Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete "{deleteDialog.item?.name}"? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsManager;
